import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSummarySchema, {
  EntityMemberSummaryJSON,
} from "_shared/entity-members/entity-member-summary-schema";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";

/**
 * A member of a legal entity as a roster shows them: one row of the paged members endpoint.
 */
export default class EntityMemberSummary {
  public static fromJSON(json: EntityMemberSummaryJSON): EntityMemberSummary {
    return new EntityMemberSummary(EntityMemberSummarySchema.EntityMemberSummaryJSONZ.parse(json));
  }

  public static fromJSONArray(jsonArray: EntityMemberSummaryJSON[]): EntityMemberSummary[] {
    return jsonArray.map((json) => EntityMemberSummary.fromJSON(json));
  }

  public readonly userId: Guid;
  public readonly legalEntityId: Guid;
  public readonly individualId?: Guid;
  public readonly firstName: string;
  public readonly lastName: string;
  public readonly email: string;
  public readonly avatarId?: Guid;
  public readonly hasCredentials: boolean;
  public readonly hasSigningAuthority: boolean;
  public readonly isPrincipal: boolean;
  public readonly dateJoined: AHDate;

  /** The row id a paged list keys on. A member is one user. */
  public get id(): Guid {
    return this.userId;
  }

  /** Their name, or their email address where no name has been filled in. */
  public get fullName(): string {
    const name = `${this.firstName} ${this.lastName}`.trim();
    return name || this.email;
  }

  private constructor(json: EntityMemberSummaryJSON) {
    this.userId = new Guid(json.userId);
    this.legalEntityId = new Guid(json.legalEntityId);
    this.individualId = json.individualId ? new Guid(json.individualId) : undefined;
    this.firstName = json.firstName;
    this.lastName = json.lastName;
    this.email = json.email;
    this.avatarId = json.avatarId ? new Guid(json.avatarId) : undefined;
    this.hasCredentials = json.hasCredentials;
    this.hasSigningAuthority = json.hasSigningAuthority;
    this.isPrincipal = json.isPrincipal;
    this.dateJoined = new AHDate(json.dateJoined);
  }

  public toEntityMember(): EntityMember {
    return EntityMember.create({ userId: this.userId, legalEntityId: this.legalEntityId });
  }

  /**
   * The member as the individual a picker hands back, built from what the roster carries. Undefined
   * when they have no individual or no name, which `Individual` requires.
   */
  public asIndividual(): Individual | undefined {
    if (!this.individualId || !this.firstName || !this.lastName) return undefined;
    return Individual.create({
      id: this.individualId,
      userId: this.userId,
      isVisible: false,
      legalEntityId: this.legalEntityId,
      hasSigningAuthority: this.hasSigningAuthority,
      profile: {
        firstName: this.firstName,
        lastName: this.lastName,
        avatarId: this.avatarId,
        email: EmailAddress.create(this.email),
      },
    });
  }
}
